/**
 * Register Screen - New user registration
 *
 * Allows users to create a new account with username, email, and password.
 * NOW THEME-AWARE
 */

import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { register } from '../../services/authApi';
import { useNavigation } from '@react-navigation/native';
import { showError } from '../../utils/toast';
import { useTheme } from '../../contexts/ThemeContext';
import { validateUsername, isValidEmail, validatePassword } from '../../utils/validation';
import { useApiErrorMessage } from '../../hooks/useApiErrorMessage';

export function RegisterScreen() {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigation = useNavigation();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const getApiErrorMessage = useApiErrorMessage();

  // Create themed styles
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const handleRegister = async () => {
    // Validate username
    const usernameValidation = validateUsername(username);
    if (!usernameValidation.isValid) {
      showError(usernameValidation.errorMessage!);
      return;
    }

    // Validate email
    if (!isValidEmail(email)) {
      showError(t('validation.invalidEmail'));
      return;
    }

    // Validate password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      showError(passwordValidation.errorMessage!);
      return;
    }

    if (password !== confirmPassword) {
      showError(t('validation.passwordsDoNotMatch'));
      return;
    }

    setLoading(true);
    try {
      await register({ username, email, password });
      // No token yet: the hard gate means the account exists but cannot log in
      // until a code is redeemed. Hand the address to VerifyEmail so it can
      // prefill and resend. The route name is cast with `as never` because this
      // screen uses the untyped useNavigation() — the sibling 'Login' link below
      // is cast the same way even though Login is already in the param list. Task
      // 26 adds VerifyEmail to AuthNavigatorParamList; the cast stays regardless.
      (navigation.navigate as any)('VerifyEmail', { email });
    } catch (error) {
      showError(getApiErrorMessage(error, 'auth.registrationFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t('auth.createAccount')}</Text>
      <Text style={styles.subtitle}>{t('auth.signUpToContinue')}</Text>

      <View style={styles.form}>
        <Input
          label={t('auth.username')}
          value={username}
          onChangeText={setUsername}
          placeholder={t('auth.enterYourUsername')}
          autoCapitalize="none"
          maxLength={255}
        />

        <Input
          label={t('auth.email')}
          value={email}
          onChangeText={setEmail}
          placeholder={t('auth.enterYourEmail')}
          keyboardType="email-address"
          autoCapitalize="none"
        />

        <Input
          label={t('auth.password')}
          value={password}
          onChangeText={setPassword}
          placeholder={t('auth.enterYourPassword')}
          secureTextEntry
        />

        <Input
          label={t('auth.confirmPassword')}
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          placeholder={t('auth.reenterYourPassword')}
          secureTextEntry
        />

        <Button
          title={t('auth.register')}
          onPress={handleRegister}
          loading={loading}
          style={styles.registerButton}
        />

        <TouchableOpacity onPress={() => navigation.navigate('Login' as never)}>
          <Text style={styles.loginText}>
            {t('auth.alreadyHaveAccount')}{' '}
            <Text style={styles.loginLink}>{t('auth.loginLink')}</Text>
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
      justifyContent: 'center',
      padding: 20,
      backgroundColor: theme.colors.background,
    },
    title: {
      fontSize: 32,
      fontWeight: 'bold',
      marginBottom: 8,
      textAlign: 'center',
      color: theme.colors.text,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
    },
    subtitle: {
      fontSize: 16,
      color: theme.colors.textSecondary,
      marginBottom: 32,
      textAlign: 'center',
    },
    form: {
      width: '100%',
    },
    registerButton: {
      marginTop: 16,
    },
    loginText: {
      marginTop: 16,
      textAlign: 'center',
      color: theme.colors.textSecondary,
    },
    loginLink: {
      color: theme.colors.primary,
      fontWeight: '600',
    },
  });
}
